/**
 * Referral Invite Page - Trade Loop v2.0
 *
 * Public landing page for referral invitation links
 * Shows basic referral info and prompts user to sign up/login to claim
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AppStoreBadges, { APP_STORE_BADGES_VISIBLE } from "@/components/AppStoreBadges";
import tradeLoopLogo from "@/assets/logo-no-text.svg";
import {
  MapPin,
  Clock,
  AlertTriangle,
  Zap,
  UserPlus,
  LogIn,
  XCircle,
  CheckCircle,
  ArrowRight,
  Loader2,
  FileText,
} from "lucide-react";

interface ReferralInviteInfo {
  senderName: string;
  jobDescription: string;
  jobLocation: string;
  urgencyLevel: string;
  invitedName?: string;
  invitedEmail?: string;
  valid: boolean;
  alreadyClaimed: boolean;
}

export default function ReferralInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState<ReferralInviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper function to extract city and state from full address
  const getCityAndState = (fullAddress: string): string => {
    // Address format: "street, city, state zip"
    // We want to extract just "city, state"
    const parts = fullAddress.split(',').map(part => part.trim());

    if (parts.length >= 2) {
      // Get the last part (should be "state zip")
      const lastPart = parts[parts.length - 1];
      // Extract just state (first word before zip code)
      const state = lastPart.split(' ')[0];
      // Get city (second to last part)
      const city = parts[parts.length - 2];

      return `${city}, ${state}`;
    }

    // Fallback: return original if parsing fails
    return fullAddress;
  };

  // Fetch invitation details
  useEffect(() => {
    const fetchInviteInfo = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/api/workflows/referral/invitation/${token}`);
        // Backend wraps response in { success: true, data: {...} }
        // api.get() returns the parsed JSON, so response = { success: true, data: {...} }
        setInviteInfo(response.data);
      } catch (err: any) {
        console.error('[ReferralInvite] Error fetching invite:', err);
        if (err.response?.status === 404) {
          setError("This invitation link is invalid or has expired.");
        } else if (err.response?.status === 410) {
          setError("This referral has already been claimed.");
        } else {
          setError("Unable to load invitation details. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInviteInfo();
  }, [token]);

  // Urgency icon and color
  const getUrgencyDisplay = (level: string) => {
    switch (level) {
      case 'urgent':
        return {
          icon: Zap,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Urgent'
        };
      case 'high':
        return {
          icon: AlertTriangle,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          label: 'High Priority'
        };
      case 'normal':
        return {
          icon: Clock,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          label: 'Normal'
        };
      default:
        return {
          icon: Clock,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: 'Low Priority'
        };
    }
  };

  const handleSignUp = () => {
    navigate(`/login?mode=signup&referralInviteToken=${token}`);
  };

  const handleLogin = () => {
    navigate(`/login?referralInviteToken=${token}`);
  };

  // Loading state
  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading invitation details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !inviteInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-700">Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-gray-600 text-lg">{error}</p>
            <Button
              onClick={() => navigate("/")}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already claimed
  if (inviteInfo.alreadyClaimed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-amber-600" />
            </div>
            <CardTitle className="text-2xl text-amber-700">Already Claimed</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-gray-600 text-lg">
              This referral has already been claimed by another user.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate("/login")}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
              >
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const urgencyDisplay = getUrgencyDisplay(inviteInfo.urgencyLevel);
  const UrgencyIcon = urgencyDisplay.icon;

  // Valid invitation - show landing page
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg pb-8">
          {/* The Trade Loop mark, not a generic briefcase glyph. For most people this page is the
              very first thing they ever see of the product — it arrives as a link in a text
              message from someone they know — so the one image on it should be the brand they are
              about to be asked to sign up for. White disc rather than the old blue gradient
              because the mark is itself blue and green; a coloured backing would swallow it. */}
          <div className="w-20 h-20 bg-white rounded-full shadow-sm ring-1 ring-blue-100 flex items-center justify-center mx-auto mb-4">
            <img src={tradeLoopLogo} alt="Trade Loop" className="w-11 h-11" draggable={false} />
          </div>
          <CardTitle className="text-3xl text-gray-900 mb-3">
            You've Been Referred!
          </CardTitle>
          <p className="text-gray-600 text-lg">
            {inviteInfo.senderName} has a business opportunity for you
          </p>
        </CardHeader>

        <CardContent className="p-8 space-y-8">
          {/* Personalized Message */}
          {inviteInfo.invitedName && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-900 font-medium">
                Hi {inviteInfo.invitedName}! 👋
              </p>
              <p className="text-blue-700 text-sm mt-1">
                This referral was specifically sent to you by {inviteInfo.senderName}
              </p>
            </div>
          )}

          {/* Job Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-lg">Referral Details</h3>

            {/* Job Description */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Project Description</p>
                  <p className="text-gray-900">{inviteInfo.jobDescription}</p>
                </div>
              </div>

              {/* Location - City and State only (not full address) */}
              <div className="flex items-start gap-2 pt-3 border-t border-gray-200">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="text-gray-900">{getCityAndState(inviteInfo.jobLocation)}</p>
                </div>
              </div>

              {/* Urgency */}
              <div className="flex items-start gap-2 pt-3 border-t border-gray-200">
                <UrgencyIcon className={`w-5 h-5 ${urgencyDisplay.color} mt-0.5 flex-shrink-0`} />
                <div>
                  <p className="text-sm font-medium text-gray-500">Priority Level</p>
                  <Badge
                    variant="outline"
                    className={`${urgencyDisplay.bgColor} ${urgencyDisplay.color} ${urgencyDisplay.borderColor}`}
                  >
                    {urgencyDisplay.label}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 text-lg mb-2">
                Ready to Claim This Referral?
              </h3>
              <p className="text-gray-600 mb-6">
                Sign up for Trade Loop to accept this referral and connect with {inviteInfo.senderName}
              </p>

              {/* Action Buttons */}
              {isAuthenticated ? (
                <Button
                  onClick={() => navigate(`/referrals?claimToken=${token}`)}
                  className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Claim This Referral
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={handleSignUp}
                    className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Sign Up to Claim
                  </Button>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Already have an account?</p>
                    <Button
                      onClick={handleLogin}
                      variant="outline"
                      className="w-full h-10"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Login to Claim
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Get the mobile app.
                Below the claim CTA rather than beside it: claiming the referral is the job this
                page exists for, and the store links are how the person keeps using Trade Loop
                afterwards — not a competing thing to tap first. Recipients land here from a text
                message on a phone, which is exactly the audience for whom "there is an app" is
                worth saying out loud.

                Same component the marketing footer and the login page use, so the artwork, the
                clear-space rules and the store URLs stay in one place. It renders nothing inside
                the native apps, and the heading is gated on the same constant so an installed
                user is not shown a labelled empty space. */}
            {APP_STORE_BADGES_VISIBLE && (
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-3">Trade Loop on your phone</p>
                <AppStoreBadges className="justify-center" />
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900 mb-1">What happens next?</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Create your free Trade Loop account</li>
                    <li>• The referral will be automatically linked to you</li>
                    <li>• Connect with {inviteInfo.senderName} to discuss the project</li>
                    <li>• Start building your network and receiving more referrals</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
